import { RemoteLogger } from "./remoteLogger";

/**
 * Base API Service for handling HTTP requests
 */

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  status: number;
}

const BASE_URL = process.env.API_BASE_URL || "https://outlook.staging.gettru.app/api";

// Never write passwords, codes or tokens to the log (request and response bodies are logged)
const SENSITIVE_KEYS = new Set([
  "password", "currentPassword", "newPassword", "otp", "secret",
  "token", "accessToken", "refreshToken", "authenticationToken",
]);
const redact = (value: any): any => {
  if (Array.isArray(value)) return value.map(redact);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, SENSITIVE_KEYS.has(k) ? "***" : redact(v)])
    );
  }
  return value;
};

export class ApiService {
  private static async request<T>(
    endpoint: string,
    method: string,
    body?: any,
    token?: string
  ): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const fullUrl = endpoint.startsWith("http") ? endpoint : `${BASE_URL}${endpoint}`;
    const safeBody = redact(body);
    RemoteLogger.info(`➡ ${method} ${fullUrl} | Stringified body: ${JSON.stringify(safeBody)}`, safeBody);

    try {
      const response = await fetch(fullUrl, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = await response.json();

      RemoteLogger.info(`✅ Response ${response.status} from ${fullUrl}`, redact(data));

      if (!response.ok) {
        RemoteLogger.warn(`❌ Error ${response.status}:`, redact(data));
        return {
          error: data.message || "An error occurred",
          status: response.status,
        };
      }

      return {
        data: data as T,
        status: response.status,
      };
    } catch (error) {
      RemoteLogger.error("💥 Network/Fetch Error:", error);
      return {
        error: "Network error or server unreachable",
        status: 500,
      };
    }
  }

  static async get<T>(endpoint: string, token?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, "GET", undefined, token);
  }

  static async post<T>(endpoint: string, body: any, token?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, "POST", body, token);
  }

  static async patch<T>(endpoint: string, body: any, token?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, "PATCH", body, token);
  }

  
  static async postFormData<T>(
    endpoint: string,
    formData: FormData,
    token?: string
  ): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const fullUrl = endpoint.startsWith("http") ? endpoint : `${BASE_URL}${endpoint}`;
    RemoteLogger.info(`➡ POST (multipart) ${fullUrl}`);

    try {
      const response = await fetch(fullUrl, {
        method: "POST",
        headers, // no Content-Type — browser sets it automatically with boundary
        body: formData,
      });

      const data = await response.json();
      RemoteLogger.info(`✅ Response ${response.status} from ${fullUrl}`, data);

      if (!response.ok) {
        return { error: data.message || "An error occurred", status: response.status };
      }

      return { data: data as T, status: response.status };
    } catch (error) {
      RemoteLogger.error("💥 Network/Fetch Error:", error);
      return { error: "Network error or server unreachable", status: 500 };
    }
  }
}
